import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  FolderPlus, CheckCircle2, Loader2,
  Video, FileType, FileText, Upload, Music, Image as ImageIcon,
} from 'lucide-react'
import { MediaCropField, type MediaCropFieldHandle } from '@meno/ui/components/shared/MediaCropModal'
import { bindUploadSignal } from '@meno/ui/lib/uploadCancellation'
import { Button } from '@meno/ui/components/ui/button'
import { Label } from '@meno/ui/components/ui/label'
import { TextInput } from '@meno/ui/components/shared/TextInput'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@meno/ui/components/ui/dialog'
import { formatBytes } from '@meno/ui/lib/utils'
import { AssetSpecsHint } from '@meno/ui/components/shared/AssetSpecsHint'
import PdfPreview from '@/components/PdfPreview'
import type { MediaFile } from './utils'

// PDF add-mode: keep as one paged document, or rasterize each page into a
// folder of images. Shared by the upload modal (below) and MediaPage's confirm.
export type PdfUploadMode = 'document' | 'folder'

// ── fileKindMeta ───────────────────────────────────────────────────────
// Per-kind icon + accent colour for a staged upload. Each media type gets its
// own distinct, legible colour (matching the card badges) — never the brand red
// / primary tint, and never a red-on-red or transparent-red swatch.
type KindMeta = {
  Icon: typeof ImageIcon
  color: string   // icon colour
  bg: string      // icon chip background
  label: string   // human label
  mime: string    // mime shown in the file-type card
}

function fileKindMeta(file: File, t: TFunction): KindMeta {
  const type = file.type || ''
  const isPdf = type === 'application/pdf' || /\.pdf$/i.test(file.name)
  if (isPdf)
    return { Icon: FileText, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/30', label: t('common.document', 'Document'), mime: type || 'application/pdf' }
  if (type === 'image/gif')
    return { Icon: ImageIcon, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30', label: t('media.gif', 'GIF'), mime: type }
  if (type.startsWith('image'))
    return { Icon: ImageIcon, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30', label: t('common.image', 'Image'), mime: type }
  if (type.startsWith('video'))
    return { Icon: Video, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30', label: t('common.video', 'Video'), mime: type }
  if (type.startsWith('audio'))
    return { Icon: Music, color: 'text-pink-600 dark:text-pink-400', bg: 'bg-pink-100 dark:bg-pink-900/30', label: t('common.audio', 'Audio'), mime: type }
  return { Icon: FileType, color: 'text-muted-foreground', bg: 'bg-muted', label: t('common.file', 'File'), mime: type || t('common.unknown', 'Unknown') }
}

// Preview payload that the upload-confirm dialog receives. The page builds
// this in handleFileSelected — preview is a base64/data: URL for images
// and a captured frame for videos.
export interface UploadPreview {
  file:      File
  preview:   string | null
  duration?: number
}

// ── UploadConfirmDialog ────────────────────────────────────────────────
// The single media-upload modal for EVERY asset type — image (with an inline
// crop step), GIF, video, audio, and PDF. The crop UI lives inside this modal
// (no separate crop dialog): a croppable image renders the shared MediaCropField
// in the left column and the cropped File is pulled on confirm. PDFs add the
// document / folder-of-images mode selector. onConfirm returns the final name,
// File (cropped for images, raw otherwise), and — for PDFs — the chosen mode.

export function UploadConfirmDialog({ preview, onConfirm, onCancel }: {
  preview:   UploadPreview
  onConfirm: (result: { name: string; file: File; pdfMode?: PdfUploadMode; asAudio?: boolean }) => void | Promise<void>
  onCancel:  () => void
}) {
  const { t } = useTranslation()

  const type        = preview.file.type
  const isPdf       = type === 'application/pdf' || /\.pdf$/i.test(preview.file.name)
  const isGif       = type === 'image/gif'
  const isCroppable = type.startsWith('image') && !isGif   // only non-gif images crop
  const isImageLike = type.startsWith('image')
  const isVideo     = type.startsWith('video')
  const isAudio     = type.startsWith('audio')

  // Default name: drop the .pdf extension for PDFs (matches the old flow), keep
  // the full filename otherwise.
  const [name, setName]       = useState(isPdf ? preview.file.name.replace(/\.pdf$/i, '') : preview.file.name)
  const [pdfMode, setPdfMode] = useState<PdfUploadMode>('document')
  // Video-only: import just the audio track into the Audio library instead of
  // adding the clip to Media. The file is stored as-is (no re-encode); players
  // play only its audio because it lands in the manifest's audio bed.
  const [asAudio, setAsAudio] = useState(false)
  const [busy, setBusy]       = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const cropRef = useRef<MediaCropFieldHandle>(null)
  const uploadAttempt = useRef<AbortController | null>(null)
  useEffect(() => () => uploadAttempt.current?.abort(), [])
  const cancelUpload = () => {
    uploadAttempt.current?.abort()
    uploadAttempt.current = null
    setBusy(false)
    onCancel()
  }

  const fmtDur = (sec: number) => `${Math.floor(sec / 60)}:${Math.floor(sec % 60).toString().padStart(2, '0')}`

  // Playable object URL for video / audio previews (revoked on unmount).
  const objectUrl = useMemo(
    () => (isVideo || isAudio) ? URL.createObjectURL(preview.file) : null,
    [preview.file, isVideo, isAudio],
  )
  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }, [objectUrl])

  const handleConfirm = async () => {
    if (uploadAttempt.current) return
    const controller = new AbortController()
    uploadAttempt.current = controller
    setBusy(true)
    setUploadError(null)
    try {
      let file = preview.file
      if (isCroppable) {
        const res = await cropRef.current?.getCropped()
        if (!res) throw new Error('The image is not ready. Please try again.')
        file = res.file
      }
      if (controller.signal.aborted) return
      bindUploadSignal(file, controller.signal)
      await onConfirm({
        name: name.trim(),
        file,
        pdfMode: isPdf ? pdfMode : undefined,
        asAudio: isVideo ? asAudio : undefined,
      })
    } catch (error) {
      if (!controller.signal.aborted) setUploadError(error instanceof Error ? error.message : 'Upload failed. Please try again.')
    } finally {
      if (uploadAttempt.current === controller) {
        uploadAttempt.current = null
        setBusy(false)
      }
    }
  }

  const meta = fileKindMeta(preview.file, t)
  const HeaderIcon = meta.Icon
  const title =
      isPdf       ? t('assets.pdfDialogTitle', 'Upload PDF')
    : isImageLike ? t('assets.uploadImageTitle', 'Upload Image')
    : isVideo     ? t('assets.uploadVideoTitle', 'Upload Video')
    : isAudio     ? t('assets.uploadAudioTitle', 'Upload Audio')
    :               t('assets.uploadFileTitle', 'Upload File')

  // PDF add-mode cards. Selected state uses the PDF accent (orange) — never a
  // red / transparent-red highlight.
  const pdfOptions: { value: PdfUploadMode; icon: typeof FileText; title: string; help: string }[] = [
    {
      value: 'document', icon: FileText,
      title: t('assets.pdfModeDocument', 'Document'),
      help: t('assets.pdfModeDocumentHelp', 'Keep the PDF as one file. Screens page through it automatically; you can preview and assign it like any asset.'),
    },
    {
      value: 'folder', icon: FolderPlus,
      title: t('assets.pdfModeFolder', 'Folder of images'),
      help: t('assets.pdfModeFolderHelp', 'Turn every page into an image and file them in a new folder. Pages then work like any image — drop them in layouts or schedule them individually.'),
    },
  ]

  return (
    <Dialog open onOpenChange={open => { if (!open) cancelUpload() }}>
      {/* Full-screen on a phone: cropping needs every pixel of the screen. */}
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto max-sm:h-dvh max-sm:max-h-dvh max-sm:w-screen max-sm:max-w-none max-sm:rounded-none">
        {/* Title + description span the full width, above the two columns.
            `pe-8` clears the dialog's absolute close (×) button so the specs
            hint on the right doesn't sit under it. */}
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pe-8">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.bg}`}>
                <HeaderIcon className={`w-5 h-5 ${meta.color}`} />
              </div>
              <div>
                <DialogTitle>{title}</DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {t('assets.uploadSubtitle', 'Upload a file to use in your screens or assets.')}
                </p>
              </div>
            </div>
            {/* Recommended sizes for horizontal / vertical / video uploads. */}
            <AssetSpecsHint />
          </div>
        </DialogHeader>

        <div className="grid gap-5 sm:grid-cols-2">
          {/* ── Left: preview — the crop step lives here for images ── */}
          <div className="space-y-3">
            {isCroppable ? (
              // Inline crop field (ratio picker + resizable box + zoom). Its
              // cropped File is pulled on confirm via cropRef.
              <MediaCropField ref={cropRef} file={preview.file} defaultRatio="16:9" height={340} />
            ) : isPdf ? (
              // Full document — every page rasterized and stacked in a
              // scrollable surface so the operator can read the whole PDF, not
              // just a first-page thumbnail.
              <div className="w-full h-[460px] rounded-lg overflow-hidden border border-border/40">
                <PdfPreview file={preview.file} className="w-full h-full" />
              </div>
            ) : (
              <div className="w-full h-[380px] relative rounded-lg overflow-hidden bg-muted/40 border border-border/40 flex items-center justify-center">
                {isImageLike && preview.preview ? (
                  <img src={preview.preview} alt="Preview" className="w-full h-full object-contain" />
                ) : isVideo && objectUrl ? (
                  <video src={objectUrl} controls className="w-full h-full object-contain bg-black" />
                ) : isAudio && objectUrl ? (
                  <div className="flex flex-col items-center justify-center gap-4 p-5 w-full">
                    <Music className="w-10 h-10 text-pink-500" />
                    <audio src={objectUrl} controls className="w-full" />
                  </div>
                ) : (
                  <HeaderIcon className={`w-10 h-10 ${meta.color}`} />
                )}
              </div>
            )}
          </div>

          {/* ── Right: info + options ── */}
          <div className="space-y-4">
            <TextInput
              label={t('screens.fileName', 'File name')}
              value={name}
              onChange={setName}
              required
              maxLength={255}
              lang="any"
              autoFocus
            />

            {/* File type card — proper per-kind icon + colour, no red tint. */}
            <div className="grid gap-1.5">
              <Label>{t('assets.fileType', 'File type')}</Label>
              <div className="flex items-center gap-3 rounded-lg border border-border p-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.bg}`}>
                  <HeaderIcon className={`w-4 h-4 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight">{meta.label}</p>
                  <p className="text-xs text-muted-foreground truncate">{meta.mime}</p>
                </div>
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              </div>
            </div>

            {/* Video-only: import the audio track into the Audio library. When
                on, the clip is stored as an audio asset (no re-encode) and plays
                as background sound rather than a visual. */}
            {isVideo && (
              <button
                type="button"
                role="switch"
                aria-checked={asAudio}
                onClick={() => setAsAudio(v => !v)}
                className={`w-full flex items-start gap-3 rounded-lg border p-3 text-start transition-colors ${
                  asAudio
                    ? 'border-pink-500 bg-pink-50 ring-1 ring-pink-500/30 dark:bg-pink-900/20'
                    : 'border-border bg-muted/30 hover:bg-muted/50'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${asAudio ? 'bg-pink-100 dark:bg-pink-900/30' : 'bg-muted'}`}>
                  <Music className={`w-4 h-4 ${asAudio ? 'text-pink-600 dark:text-pink-400' : 'text-muted-foreground'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight">
                    {t('assets.videoAsAudioTitle', 'Use audio only')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug">
                    {t('assets.videoAsAudioHelp', 'Add just this video’s sound to your Audio library to play as background audio. The video itself won’t be added to Media.')}
                  </p>
                </div>
                {/* Checkbox-style indicator on the trailing edge. */}
                <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                  asAudio ? 'bg-pink-500 border-pink-500' : 'border-muted-foreground/40'
                }`}>
                  {asAudio && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                </span>
              </button>
            )}

            {/* PDF-only: how to add the PDF — two mutually-exclusive cards. */}
            {isPdf && (
              <div className="grid gap-1.5">
                <Label>{t('assets.pdfModeQuestion', 'How do you want to use this PDF?')}</Label>
                <div className="grid gap-2" role="radiogroup" aria-label={t('assets.pdfModeLabel', 'How to add this PDF')}>
                  {pdfOptions.map(opt => {
                    const Icon = opt.icon
                    const active = pdfMode === opt.value
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setPdfMode(opt.value)}
                        className={`flex items-start gap-3 rounded-lg border p-3 text-start transition-colors ${
                          active
                            ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500/30 dark:bg-orange-900/20'
                            : 'border-border bg-muted/30 hover:bg-muted/50'
                        }`}
                      >
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${active ? 'text-orange-600 dark:text-orange-400' : 'text-muted-foreground'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-tight">{opt.title}</p>
                          <p className="text-xs text-muted-foreground mt-1 leading-snug">{opt.help}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* File details */}
            <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t('assets.fileDetails', 'File details')}
              </p>
              <div className="flex justify-between text-sm gap-3">
                <span className="text-muted-foreground shrink-0">{t('assets.fileNameLabel', 'File name')}</span>
                <span className="font-medium truncate">{preview.file.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('common.size', 'Size')}</span>
                <span className="font-medium tabular-nums">{formatBytes(preview.file.size)}</span>
              </div>
              {preview.duration ? (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('screens.length', 'Length')}</span>
                  <span className="font-medium tabular-nums">{fmtDur(preview.duration)}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {uploadError && <p role="alert" className="text-sm text-destructive">{uploadError}</p>}
          <Button variant="outline" onClick={cancelUpload}>{t('common.cancel')}</Button>
          <Button disabled={!name.trim() || busy} onClick={handleConfirm} className="gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {t('common.upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── RenameDialog ───────────────────────────────────────────────────────
// Single text-field prompt for renaming a media file. Submit on Enter or
// the Save button.

export function RenameDialog({ file, onSave, onClose }: {
  file:    MediaFile
  onSave:  (name: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState(file.original_name)
  return (
    <Dialog open onOpenChange={open => { if (!open) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{t('common.renameFile', 'Rename File')}</DialogTitle></DialogHeader>
        <TextInput
          label={t('common.name', 'Name')}
          value={name}
          onChange={setName}
          required
          maxLength={255}
          lang="any"
          autoFocus
          onKeyDown={e => e.key === 'Enter' && name.trim() && onSave(name.trim())}
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!name.trim()} onClick={() => onSave(name.trim())} className="gap-2">
            <CheckCircle2 className="w-4 h-4" /> {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── NewFolderDialog ────────────────────────────────────────────────────
// Name-only prompt for the "New Folder" action. Stays minimal until
// folders are wired to the backend.

export function NewFolderDialog({ onCreate, onClose }: {
  onCreate: (name: string) => void
  onClose:  () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="w-4 h-4 text-primary" />
            {t('assets.newFolder', 'New Folder')}
          </DialogTitle>
        </DialogHeader>
        <TextInput
          label={t('common.name', 'Name')}
          value={name}
          onChange={setName}
          required
          maxLength={120}
          lang="any"
          autoFocus
          placeholder={t('assets.folderNamePlaceholder', 'e.g. Campaign Q4')}
          onKeyDown={e => e.key === 'Enter' && name.trim() && onCreate(name.trim())}
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!name.trim()} onClick={() => onCreate(name.trim())} className="gap-2">
            <CheckCircle2 className="w-4 h-4" /> {t('common.create', 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
